import { useOrganization } from '@/clients/organization/useOrganization.ts';
import { useTranslation } from 'react-i18next';

type PurchasePriceHints = {
    customerOrganizationId: number;
    salesPrice: number | undefined;
    showZeroSalesPrice: boolean;
    onZeroSalesPriceChange: () => void;
};

export function SalesPriceHints({
    salesPrice,
    customerOrganizationId,
    showZeroSalesPrice,
    onZeroSalesPriceChange,
}: PurchasePriceHints) {
    if (salesPrice === undefined) {
        return;
    }

    if (salesPrice < 0) {
        return <NegativeSalesPriceHint customerOrganizationId={customerOrganizationId} />;
    }

    if (salesPrice === 0) {
        return (
            <ZeroSalesPriceHint
                customerOrganizationId={customerOrganizationId}
                isBillingRelevant={showZeroSalesPrice}
                onClick={onZeroSalesPriceChange}
            />
        );
    }
}

export function NegativeSalesPriceHint({ customerOrganizationId }: { customerOrganizationId: number }) {
    const { t } = useTranslation();
    const { data: orderingOrganization } = useOrganization(customerOrganizationId);

    return (
        <div className="font-copy-sm text-subdued">
            {t('sections.productSelection.productPositionFooter.negativeSalesInfoText', {
                orgName: orderingOrganization?.name,
            })}
        </div>
    );
}

type ZeroSalesPriceHintProps = {
    onClick: () => void;
    customerOrganizationId: number;
    isBillingRelevant: boolean;
};

function ZeroSalesPriceHint({ onClick, customerOrganizationId, isBillingRelevant }: ZeroSalesPriceHintProps) {
    const { t } = useTranslation();
    const { data: orderingOrganization } = useOrganization(customerOrganizationId);

    const descriptionLabel = isBillingRelevant
        ? t('sections.productSelection.productPositionFooter.salesPricePositionConfiguration', {
              orgName: orderingOrganization?.name,
          })
        : t('sections.productSelection.productPositionFooter.zeroSalesInfoText', {
              orgName: orderingOrganization?.name,
          });

    const buttonLabel = isBillingRelevant
        ? t('sections.productSelection.productPositionFooter.doNotShowPosition')
        : t('sections.productSelection.productPositionFooter.showPosition');

    return (
        <div className="font-copy-sm text-subdued @3xl:flex-row @3xl:justify-between flex flex-col gap-2">
            <span>{descriptionLabel}</span>
            <button
                type="button"
                className="font-copy-sm-strong cursor-pointer whitespace-nowrap border-none bg-transparent p-0"
                onClick={onClick}
                name="zero-sales-price-toggle"
                aria-checked={isBillingRelevant}
            >
                {buttonLabel}
            </button>
        </div>
    );
}
